import { useMemo, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useTemplates, useDeleteTemplate } from '@/api/hooks';
import { Input } from '@/components/Input';
import { Button } from '@/components/Button';
import { Card } from '@/components/Card';
import { Badge } from '@/components/Badge';
import { ConfirmDialog } from '@/components/ConfirmDialog';
import { Tooltip } from '@/components/Tooltip';
import { PageHeader, Skeleton, EmptyState } from '@/components/primitives';
import { useSelection, SelectionBar } from '@/components/selection';
import { FilterPopover } from '@/components/FilterPopover';
import { Select } from '@/components/Select';
import { Field } from '@/components/primitives';
import { cn } from '@/lib/utils';
import { Search, Trash2, ArrowRight, LayoutTemplate, Filter, SlidersHorizontal, AlertCircle } from 'lucide-react';
import { toast } from 'react-hot-toast';
import type { DashboardTemplateItem } from '@/api/client';

export function TemplatesPage() {
  const { data: templates, isLoading, error, refetch } = useTemplates();
  const deleteMutation = useDeleteTemplate();
  const [search, setSearch] = useState('');
  const [typeFilter, setTypeFilter] = useState('all');
  const [usageFilter, setUsageFilter] = useState<'all' | 'used' | 'unused'>('all');
  const [sort, setSort] = useState<'name' | 'usage' | 'updated'>('name');
  const sel = useSelection();
  const [pendingDelete, setPendingDelete] = useState<{ ids: string[]; label: string } | null>(null);

  const tenantTypes = useMemo(
    () => [...new Set((templates ?? []).map((t) => t.typeOfTenantID).filter(Boolean))].sort() as string[],
    [templates]
  );

  const activeFilterCount = [typeFilter, usageFilter].filter((f) => f !== 'all').length;

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    const list = (templates || [])
      .filter((t) => t.name.toLowerCase().includes(q))
      .filter((t) => typeFilter === 'all' || t.typeOfTenantID === typeFilter)
      .filter((t) => {
        if (usageFilter === 'all') return true;
        const inUse = (t.dashboards?.items?.length ?? 0) > 0;
        return usageFilter === 'used' ? inUse : !inUse;
      });
    list.sort((a, b) => {
      if (sort === 'usage') return (b.dashboards?.items?.length ?? 0) - (a.dashboards?.items?.length ?? 0);
      if (sort === 'updated') return (b.updatedAt ?? '').localeCompare(a.updatedAt ?? '');
      return a.name.localeCompare(b.name);
    });
    return list;
  }, [templates, search, typeFilter, usageFilter, sort]);
  const filteredIds = useMemo(() => filtered.map((t) => t.id), [filtered]);

  const confirmDelete = async () => {
    if (!pendingDelete) return;
    const { ids } = pendingDelete;
    let failed = 0;
    for (const id of ids) {
      try {
        await deleteMutation.mutateAsync(id);
      } catch {
        failed++;
      }
    }
    const n = ids.length;
    setPendingDelete(null);
    sel.exit();
    if (failed > 0) toast.error(`${failed} of ${n} template${n === 1 ? '' : 's'} could not be deleted`);
    else toast.success(n === 1 ? 'Template deleted' : `${n} templates deleted`);
  };

  return (
    <div className="space-y-5">
      <PageHeader
        title="Dashboard Templates"
        description="Reusable Cluvio dashboard configurations with fixed and variable filters."
        actions={
          <>
            {(templates?.length ?? 0) > 0 && (
              <Button variant="ghost" onClick={() => (sel.active ? sel.exit() : sel.start())}>
                {sel.active ? 'Cancel' : 'Select'}
              </Button>
            )}
            <Link to="/generate">
              <Button>
                Generate
                <ArrowRight className="h-4 w-4 ml-2" />
              </Button>
            </Link>
          </>
        }
      />

      <div className="flex items-center gap-2 max-w-xl">
        <div className="relative flex-1">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
          <Input value={search} onChange={(e) => setSearch(e.target.value)} placeholder="Search templates..." className="pl-9" />
        </div>
        <FilterPopover
          activeCount={activeFilterCount}
          onReset={() => {
            setTypeFilter('all');
            setUsageFilter('all');
          }}
        >
          <Field label="Tenant type">
            <Select value={typeFilter} onChange={(e) => setTypeFilter(e.target.value)} className="[&>select]:h-8 [&>select]:text-xs">
              <option value="all">Any type</option>
              {tenantTypes.map((t) => (
                <option key={t} value={t}>
                  {t}
                </option>
              ))}
            </Select>
          </Field>
          <Field label="Usage">
            <Select value={usageFilter} onChange={(e) => setUsageFilter(e.target.value as typeof usageFilter)} className="[&>select]:h-8 [&>select]:text-xs">
              <option value="all">Any usage</option>
              <option value="used">In use</option>
              <option value="unused">Unused (0 dashboards)</option>
            </Select>
          </Field>
          <Field label="Sort by">
            <Select value={sort} onChange={(e) => setSort(e.target.value as typeof sort)} className="[&>select]:h-8 [&>select]:text-xs">
              <option value="name">Name A–Z</option>
              <option value="usage">Most used</option>
              <option value="updated">Recently updated</option>
            </Select>
          </Field>
        </FilterPopover>
      </div>

      {isLoading ? (
        <Card className="p-0 overflow-hidden divide-y divide-border/60">
          {[0, 1, 2, 3].map((i) => (
            <div key={i} className="px-4 py-3 space-y-2">
              <Skeleton className="h-4 w-1/3" />
              <Skeleton className="h-3 w-2/3" />
            </div>
          ))}
        </Card>
      ) : error ? (
        <EmptyState
          icon={AlertCircle}
          title="Failed to load templates"
          description={error.message}
          action={
            <Button variant="outline" size="sm" onClick={() => refetch()}>
              Retry
            </Button>
          }
        />
      ) : filtered.length === 0 ? (
        <EmptyState
          icon={LayoutTemplate}
          title={search ? 'No templates match your search' : 'No templates yet'}
          description={search ? 'Try a different search term.' : 'Generate your first template from a Cluvio dashboard.'}
          action={
            !search ? (
              <Link to="/generate">
                <Button size="sm">Generate template</Button>
              </Link>
            ) : undefined
          }
        />
      ) : (
        <Card className="p-0 overflow-hidden">
          <div className="divide-y divide-border/60">
            {filtered.map((template, i) => (
              <TemplateRow
                key={template.id}
                template={template}
                index={i}
                selection={sel}
                orderedIds={filteredIds}
                onDelete={() => setPendingDelete({ ids: [template.id], label: `"${template.name}"` })}
              />
            ))}
          </div>
          {sel.active && (
            <div className="px-4 pb-3">
              <SelectionBar
                count={sel.selected.size}
                actionLabel="Delete"
                pending={deleteMutation.isPending}
                onAction={() =>
                  setPendingDelete({
                    ids: [...sel.selected],
                    label: `${sel.selected.size} template${sel.selected.size === 1 ? '' : 's'}`,
                  })
                }
                onDone={sel.exit}
              />
            </div>
          )}
        </Card>
      )}

      <ConfirmDialog
        open={pendingDelete !== null}
        title={`Delete ${pendingDelete?.label ?? ''}?`}
        description="This removes the template definition. Dashboards already created from it are not affected."
        confirmLabel={pendingDelete && pendingDelete.ids.length > 1 ? 'Delete all' : 'Delete'}
        destructive
        loading={deleteMutation.isPending}
        onConfirm={confirmDelete}
        onCancel={() => setPendingDelete(null)}
      />
    </div>
  );
}

function TemplateRow({
  template,
  index,
  selection,
  orderedIds,
  onDelete,
}: {
  template: DashboardTemplateItem;
  index: number;
  selection: ReturnType<typeof useSelection>;
  orderedIds: string[];
  onDelete: () => void;
}) {
  const navigate = useNavigate();
  const dashboardCount = template.dashboards?.items?.length ?? 0;
  const hasMore = template.dashboards?.nextToken != null;
  const selected = selection.selected.has(template.id);

  return (
    <div
      onClick={
        selection.active
          ? (e) => selection.toggle(template.id, index, orderedIds, e.shiftKey)
          : () => navigate(`/templates/${template.id}`)
      }
      className={cn(
        'group px-4 py-3 transition-colors cursor-pointer',
        selection.active && 'select-none',
        selection.active && selected ? 'bg-accent/60' : 'hover:bg-muted/40'
      )}
    >
      <div className="flex items-center justify-between gap-3">
        <span className="flex items-center gap-2.5 min-w-0">
          {selection.active && (
            <input
              type="checkbox"
              readOnly
              checked={selected}
              className="h-3.5 w-3.5 shrink-0 accent-primary pointer-events-none"
            />
          )}
          <Link
            to={`/templates/${template.id}`}
            onClick={(e) => selection.active && e.preventDefault()}
            className={cn('font-medium text-sm truncate hover:text-primary transition-colors', selection.active && 'pointer-events-none')}
            title={template.name}
          >
            {template.name}
          </Link>
        </span>
        <span className="flex items-center gap-1 shrink-0">
          {!selection.active && (
            <span className="flex items-center gap-0.5 opacity-100 lg:opacity-0 lg:group-hover:opacity-100 transition-opacity">
              <Tooltip label="Delete template">
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={(e) => {
                    e.stopPropagation();
                    onDelete();
                  }}
                  className="h-7 px-2 text-destructive hover:bg-destructive/10"
                >
                  <Trash2 className="h-3.5 w-3.5" />
                </Button>
              </Tooltip>
            </span>
          )}
          <Badge variant="secondary">{template.typeOfTenantID}</Badge>
        </span>
      </div>
      {template.description && (
        <p className="text-xs text-muted-foreground mt-0.5 truncate">{template.description}</p>
      )}
      <div className="flex items-center gap-x-4 gap-y-1 mt-1 text-[11px] text-muted-foreground">
        <span className="inline-flex items-center gap-1.5">
          <Filter className="h-3 w-3" />
          {template.fixedFilters.length} fixed
        </span>
        <span className="inline-flex items-center gap-1.5">
          <SlidersHorizontal className="h-3 w-3" />
          {template.variableFilters.length} variable
        </span>
        <span className="inline-flex items-center gap-1.5">
          <LayoutTemplate className="h-3 w-3" />
          {dashboardCount}
          {hasMore ? '+' : ''} dashboard{dashboardCount === 1 && !hasMore ? '' : 's'}
        </span>
      </div>
    </div>
  );
}
